import React from "react";
import {
  Box,
  Button,
  MenuItem,
  Stack,
  TextField,
  Typography,
  Paper,
  alpha,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import SendIcon from "@mui/icons-material/Send";
import { createReport } from "../services/bugReportStorageService";

const MODULE_OPTIONS = [
  "Dashboard",
  "Patient Map",
  "Care Gaps",
  "Coding Opportunities",
  "AI Clinical Notes",
  "Medication Reconciliation",
  "Specialist Referrals",
  "Lab Phlebotomy",
  "Assignments",
  "Discharged",
  "Admitted",
  "Emergency",
  "High Cost",
  "High Risk",
  "Care Plans",
  "Patient Profiles",
  "Schedule",
  "Administration",
  "Library",
  "Technical Support",
  "Other",
];

const SEVERITY_OPTIONS = [
  { value: "critical", label: "Critical" },
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
];

export default function BugReportForm({ onSuccess }) {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      module: "",
      severity: "",
      title: "",
      description: "",
      stepsToReproduce: "",
      expectedBehavior: "",
      actualBehavior: "",
    },
  });

  const onSubmit = (data) => {
    createReport(data);
    reset();
    if (onSuccess) onSuccess();
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Typography variant="h6" fontWeight={600} gutterBottom>
        Report a Bug
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Please provide as much detail as possible to help us identify and fix the issue.
      </Typography>

      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={2.5}>
          {/* Module & Severity row */}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Controller
              name="module"
              control={control}
              rules={{ required: "Module is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label="Module"
                  fullWidth
                  error={!!errors.module}
                  helperText={errors.module?.message}
                >
                  {MODULE_OPTIONS.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <Controller
              name="severity"
              control={control}
              rules={{ required: "Severity is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label="Severity"
                  fullWidth
                  error={!!errors.severity}
                  helperText={errors.severity?.message}
                >
                  {SEVERITY_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Stack>

          {/* Title */}
          <Controller
            name="title"
            control={control}
            rules={{
              required: "Title is required",
              maxLength: { value: 200, message: "Max 200 characters" },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Title"
                fullWidth
                error={!!errors.title}
                helperText={errors.title?.message}
                inputProps={{ maxLength: 200 }}
              />
            )}
          />

          {/* Description */}
          <Controller
            name="description"
            control={control}
            rules={{
              required: "Description is required",
              maxLength: { value: 2000, message: "Max 2000 characters" },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Description"
                fullWidth
                multiline
                minRows={3}
                maxRows={8}
                error={!!errors.description}
                helperText={errors.description?.message}
                inputProps={{ maxLength: 2000 }}
              />
            )}
          />

          {/* Steps to Reproduce */}
          <Controller
            name="stepsToReproduce"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Steps to Reproduce"
                fullWidth
                multiline
                minRows={2}
                maxRows={6}
                placeholder="1. Go to...&#10;2. Click on...&#10;3. See error"
              />
            )}
          />

          {/* Expected / Actual row */}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Controller
              name="expectedBehavior"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Expected Behavior"
                  fullWidth
                  multiline
                  minRows={2}
                />
              )}
            />
            <Controller
              name="actualBehavior"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Actual Behavior"
                  fullWidth
                  multiline
                  minRows={2}
                />
              )}
            />
          </Stack>

          {/* Submit */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 1 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting}
              startIcon={<SendIcon />}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                px: 4,
                py: 1.2,
                fontWeight: 600,
                background: "linear-gradient(135deg, #EF4444 0%, #F97316 100%)",
                "&:hover": {
                  background: "linear-gradient(135deg, #DC2626 0%, #EA580C 100%)",
                },
              }}
            >
              Submit Report
            </Button>
          </Box>
        </Stack>
      </Box>
    </Paper>
  );
}
